import { operate, type Result } from "@/config";
import { payload } from "@/hooks/shared/useOperate";

/**
 * Settings, sessions and notifications.
 *
 * Thin by design: read the form, call the operation, hand back the API's own
 * result. Authorisation, validation, the database write and the audit row all
 * happen in the API — there is deliberately no second implementation of any rule
 * about who may do what.
 *
 * The result is passed through untouched, `fieldErrors` and all. That chain is
 * the fix for the prototype's single `catch { setError(t('common.error')) }`,
 * which collapsed `email_in_use`, `invalid_supervisor`, `missing_fields` and
 * `upload_error` into one "something went wrong" — duplicate email being by far
 * the likeliest real failure.
 */

export type FormResult<T = undefined> = Result<T>;

export function saveOrgSettings(formData: FormData): Promise<FormResult<undefined>> {
  return operate<undefined>("settings.saveOrg", payload(formData));
}

export function saveProfile(formData: FormData): Promise<FormResult<undefined>> {
  return operate<undefined>("settings.saveProfile", payload(formData));
}

export function changePassword(formData: FormData): Promise<FormResult<undefined>> {
  return operate<undefined>("settings.changePassword", payload(formData));
}

export function endSession(formData: FormData): Promise<FormResult<undefined>> {
  return operate<undefined>("settings.endSession", payload(formData));
}

export function markNotification(formData: FormData): Promise<FormResult<undefined>> {
  return operate<undefined>("notifications.mark", payload(formData));
}

export function endOtherSessions(): Promise<FormResult<{ count: number }>> {
  return operate<{ count: number }>("settings.endOtherSessions", {});
}

export function markAllNotifications(): Promise<FormResult<{ count: number }>> {
  return operate<{ count: number }>("notifications.markAll", {});
}
